import { formatCurrency } from './formatCurrency';

/**
 * U+2014 EM DASH — THE one definition; `utils/orderListPdf.js` (the precedent
 * this propagated) imports it since 2026-09-23. 🪤 Not U+2013 (en dash) and not the
 * ASCII hyphen: see the marker table below for why the hyphen loses.
 *
 * 🪤 Declared ABOVE `moneyOrAbsent` on purpose. It is that function's default
 * parameter, and a default is evaluated at CALL time, so a `const` below would
 * work — right up until someone calls the helper from module scope in this
 * file and gets a temporal-dead-zone `ReferenceError`. Order removes the trap.
 */
export const EM_DASH = '—';

/**
 * THE one answer to "what does a money figure render as when there might not
 * be one" — for a STANDALONE display: a table cell, a bar label, a money row.
 *
 * ⚖️ **Owner ruling, Pete, 2026-09-21** — shown both options and choosing this
 * one: *"Guard the absence marker AT THE CALL SITES. Leave `formatCurrency` a
 * dumb converter."* `formatCurrency` renders an ABSENT amount as a real,
 * actionable `£0.00`, and that is deliberate there, not a bug: the marker
 * cannot live inside it because seven of its call sites interpolate the result
 * into the MIDDLE of a sentence, where a bare dash produces *"— estimated —
 * not yet added to the budget"*. **This helper is the call-site guard, shared;
 * it is not the marker moving back into the converter.**
 *
 * 🔑 **THREE STATES, NOT TWO. This is the whole definition and the reason the
 * helper exists as one thing rather than three.**
 *
 *   1. `null` / `undefined` / `''`        → **ABSENT**    → `absentMarker`
 *   2. present but `Number()` non-finite  → **MALFORMED** → a loud `£NaN`
 *   3. any finite number, **`0` INCLUDED** → the money    → `£0.00`, `-£75.00`
 *
 * 🚨 **A REAL, DELIBERATE `0` IS NOT AN ABSENCE.** £0 is a legitimate value
 * elsewhere in this app by owner ruling (see `MarketplaceZeroRate.test.js`), so
 * **every falsy test on a money value is a defect** — `!amount`, `amount || 0`
 * and `amount ? a : b` all convert a real zero into "nothing here". The repo's
 * standing rule, from `constants/CLAUDE.md`: *a falsy value that is also a
 * legitimate answer can never double as the absence marker.* 🪤 And check the
 * falsy TYPE when you test it — the STRING `'0'` is TRUTHY, so a string-only
 * fixture certifies an implementation one refactor away from dropping the
 * value. Both are pinned in `moneyOrAbsent.test.js`.
 *
 * 🚨 **ABSENT AND MALFORMED DELIBERATELY DO NOT SHARE A MARKER**, and this is
 * the half that is easy to "tidy" away. Generalise the rule above: *a marker
 * that means "absent" cannot also mean "malformed", or it is evidence of
 * neither.* The measured case: a £1,000 budget can reach the browser as the
 * STRING `"1,000"`, and `Number('1,000')` is `NaN`. Collapsing the two
 * conditions into one would render a real, entered thousand pounds as "nobody
 * set this" — taking a defect that is **wrong but VISIBLE** and making it
 * silent, which is the opposite of what the money work exists to do. State 2
 * goes back through `formatCurrency` deliberately, so the malformed output can
 * never drift from what a malformed string already produces.
 *
 * 📐 **`absentMarker` IS A PARAMETER BECAUSE THE HOUSE HAS FOUR MARKERS AND
 * THE DIFFERENCES ARE DELIBERATE — do not standardise them.**
 *   • `EM_DASH` (the default) — a standalone figure. Chosen over a plain `'-'`
 *     on evidence, not taste: `utils/orderListPdf.js` already converts a `'-'`
 *     INTO its `EM_DASH`, so the house ranks them; and a hyphen beside money
 *     reads as a MINUS SIGN, in an app that renders `-£75.00` and `−£75.00`.
 *     🔻 **CORRECTED 2026-09-22 — the first form was `£-75.00` until the owner
 *     ruled the sign belongs outside the symbol.** The two forms still differ,
 *     deliberately and now only in the DASH CHARACTER: `formatCurrency` emits a
 *     hyphen-minus for a negative AMOUNT, while `HireAdjustmentDecisionDialog`
 *     and `BudgetOverview` build a `+`/`−` SIGNED DELTA from `Math.abs()` and a
 *     U+2212 MINUS SIGN. A delta has a `+` case that a plain amount has no use
 *     for, so they are two presentations of two different things and are NOT
 *     unified. The em-dash argument above is unaffected either way.
 *   • `'-'` — `GagDetailPanel`'s read-only Estimated cost field, which matches
 *     ITS NEIGHBOUR (`estimated_days ?? '-'`) rather than this file's default.
 *   • `null` — `utils/gagCsvExport.js`, where the marker must be an EMPTY CSV
 *     FIELD, not a dash a spreadsheet would import as text.
 *   • **the whole clause suppressed** — the MID-SENTENCE sites, which is why
 *     `GagDetailPanel`'s `budgetAmountSuffix` is **NOT** built on this helper
 *     and must not be folded into it: it returns a SUFFIX CLAUSE (`" (£X)"` or
 *     `""`), not a standalone marker, so it has a different return contract.
 *     `utils/gagBudgetStatus.js` is the same shape. **A helper that tried to
 *     serve both would need the caller to pass a sentence template, at which
 *     point the caller may as well write the `if`.**
 *
 * 🔻 **WHY THIS IS SHARED RATHER THAN INLINED THREE TIMES, AND THE HONEST
 * REASON.** The guard was written inline in three files first, and
 * `npm run check-duplication` reported **0 undeclared** with no group touching
 * any of them. That was a true statement about the CHECKER and not about the
 * code: it is a whole-function Jaccard scanner at `minTokens=20`, so a
 * three-line guard embedded inside a larger, differently-shaped function is
 * invisible to it, and a small standalone one is under the threshold.
 * 🕶️ **A SCAN'S COVERAGE IS ITS PATTERNS, NEVER ITS HIT COUNT** — "the checker
 * reports none" and "there is none" are different claims, and three literal
 * copies of a money guard is the wrong artefact to leave behind whatever the
 * gate says.
 *
 * @param {number|string|null|undefined} amount
 * @param {string|null} [absentMarker] - what an ABSENT amount renders as.
 * @returns {string|null}
 */
export function moneyOrAbsent(amount, absentMarker = EM_DASH) {
  if (amount == null || amount === '') return absentMarker;
  if (!Number.isFinite(Number(amount))) return formatCurrency(String(amount));
  return formatCurrency(amount);
}
